// One row in a "confirm a sequence" tick-list - reused verbatim for BOTH the guided
// workflow-validation walkthrough (Flow 4, D35) and the Production Build hardening
// checklist (Flow 6, ux spec §9.1 "deliberately consistent, not three different
// widgets for the same shape"). Extends `Stepper.tsx`'s `SubStepper` tick language from
// a fixed three-item set into an arbitrary founder-meaningful list rendered as full rows
// (docs/phase-0/slice5-component-designs.md §3.4 `.workflow-item`). Confirm/Adjust carry
// the item's own text in their accessible name (never bare "Confirm"/"Adjust" repeated
// down the list, ux spec §7.4).
import { useId, useState } from "react";
import { buildCopy } from "../copy/build.js";

export const BuildChecklistItem = ({
  statement,
  resolved,
  disabledReason,
  onConfirm,
  onAdjust,
}: {
  readonly statement: string;
  readonly resolved: boolean;
  /** Present only for a viewer (role-gated); omitted entirely for an owner/admin. */
  readonly disabledReason?: string;
  readonly onConfirm: () => void;
  readonly onAdjust: (nextStatement: string) => void;
}): React.JSX.Element => {
  const [adjusting, setAdjusting] = useState(false);
  const [draft, setDraft] = useState(statement);
  const disabled = disabledReason !== undefined;
  const fieldId = useId();
  const reasonId = useId();

  if (adjusting) {
    return (
      <li className="workflow-item">
        <label className="field-label" htmlFor={fieldId}>
          {buildCopy("build.workflow.adjustPrompt")}
        </label>
        <textarea
          id={fieldId}
          className="field"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
        />
        <div className="row gap8 mt8">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={draft.trim().length === 0}
            onClick={() => {
              onAdjust(draft.trim());
              setAdjusting(false);
            }}
          >
            {buildCopy("build.workflow.saveAdjustmentCta")}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setDraft(statement);
              setAdjusting(false);
            }}
          >
            Cancel
          </button>
        </div>
      </li>
    );
  }

  if (resolved) {
    return (
      <li className="workflow-item">
        <p style={{ margin: 0, fontSize: 14 }}>{statement}</p>
        <div className="row aic gap8 mt8">
          <span className="status-chip good">{buildCopy("build.workflow.confirmedChip")}</span>
          <button
            type="button"
            className="turn-lang-toggle"
            disabled={disabled}
            onClick={() => {
              setAdjusting(true);
            }}
          >
            {buildCopy("build.workflow.editLink")}
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className="workflow-item">
      <p style={{ margin: 0, fontSize: 14 }}>{statement}</p>
      <div className="row gap8 mt8 tooltip-wrap wrap-g">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={disabled}
          aria-describedby={disabled ? reasonId : undefined}
          onClick={onConfirm}
        >
          {buildCopy("build.workflow.confirmCta", { statement })}
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={disabled}
          onClick={() => {
            setAdjusting(true);
          }}
        >
          {buildCopy("build.workflow.adjustCta", { statement })}
        </button>
        {disabled ? (
          <span className="tooltip-bubble" id={reasonId} role="tooltip">
            {disabledReason}
          </span>
        ) : null}
      </div>
    </li>
  );
};
